import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import Big from 'big.js'
import {countries, type TCountryCode} from 'countries-list'
import {Breadcrumb, Breadcrumbs} from 'react-aria-components'
import {groupBy} from 'remeda'

import failureSvg from '@/assets/icons/failure.svg'
import successSvg from '@/assets/icons/success.svg'
import spinnerLottie from '@/assets/lotties/spinner.lottie'
import OrderItemGroups from '@/components/OrderItemGroups/OrderItemGroups'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import {PURCHASE_EVENT} from '@/constants/events'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import {getOrderQueryOptions} from '@/lib/order/hooks/useOrder'
import useCashback from '@/lib/payment/hooks/useCashback'
import {type CartItem, CheckoutResult} from '@/models/cartModel'
import type {Order} from '@/models/orderModel'
import {CartRoute, CheckoutRoute, HomeRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {formatLocaleDateTime} from '@/utils/datetime'
import {logEvent} from '@/utils/logger'
import RACLink from '@/utils/router/RACLink'
import NotFound from '@/views/NotFound/NotFound'

import style from './Checkout.module.scss'

function Result({
  parsedResult,
  order,
}: {
  parsedResult: CheckoutResult
  order: Order & {
    value: Big
    items: Record<PropertyKey, [CartItem, ...CartItem[]]>
  }
}) {
  const navigate = useNavigate()

  const onDone = useCallback(() => {
    void navigate({
      to: HomeRoute.fullPath,
    })
  }, [navigate])

  const subtotal = order.value
  const discountValue = order.moreInfo?.amountDiscount
    ? Big(-1).times(Big(order.moreInfo.amountDiscount))
    : Big(0)
  const deliveryFee = Big(order.moreInfo?.deliveryFee ?? 0)
  const totalBeforeTax = subtotal.add(deliveryFee).add(discountValue)
  const tax = Big(order.paymentInfo.amountTax ?? 0)
  const roundUp = Big(order.moreInfo?.amountRoundUp ?? 0)
  const total = totalBeforeTax.add(tax).add(roundUp)

  const {data: cashback} = useCashback(total)

  useEffect(() => {
    if (parsedResult === CheckoutResult.Success) {
      logEvent(...PURCHASE_EVENT)
    }
  }, [parsedResult])

  return (
    <div className={style.Result}>
      <div role='alert'>
        <img
          src={parsedResult === CheckoutResult.Success ? successSvg : failureSvg}
          alt=''
          className={style.ResultDecoration}
        />
        <h1 className={style.Heading}>
          <span role='alert'>
            {parsedResult === CheckoutResult.Success ? 'Thank you for the order' : 'Payment failed'}
          </span>
        </h1>
        <div className={style.Description}>
          {parsedResult === CheckoutResult.Success ? (
            <>
              We have sent an email to <span className={style.Email}>{order.email}</span> with
              everything you need to know about your order
            </>
          ) : (
            <>
              {`Unfortunately, the payment for this order has failed.
An issuing bank will often decline an attempt to charge a card if the name, expiry date, or post code you entered doesn't match the bank's information.
In order to proceed with your order and avoid cancellation, we kindly ask you to change your payment method to credit card within 5 days.* Please note that Amazon.de, in order to be able to ship orders as quickly as possible, does generally not accept advance payment methods.
Please visit your Order Details to update the payment information for your order.`}
            </>
          )}
        </div>
      </div>
      <div className={style.Divider} />
      <div className={style.OrderDetails}>
        <div className={style.Header}>
          <h2>Order details</h2>
          <div className={style.OrderId}>
            <VisuallyHidden>Order ID:</VisuallyHidden>#{order.id}
          </div>
          {/* TODO: copy */}
        </div>
        <div className={style.ShippingInfo}>
          <div className={style.NameAndTime}>
            <div className={style.Name}>
              <VisuallyHidden>Name:</VisuallyHidden>
              {order.shippingInfo?.name}
            </div>
            <div className={style.Time}>
              Placed on{' '}
              {formatLocaleDateTime(order.createdAt, 'en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>
          {order.shippingInfo && (
            <div className={style.Address}>
              <div className={style.AddressItem}>
                <VisuallyHidden>Address line 1:</VisuallyHidden>
                {order.shippingInfo.line1}
              </div>
              {order.shippingInfo.line2 && (
                <div className={style.AddressItem}>
                  <VisuallyHidden>Address line 2:</VisuallyHidden>
                  {order.shippingInfo.line2}
                </div>
              )}
              <div className={style.AddressItem}>
                <VisuallyHidden>City:</VisuallyHidden>
                {order.shippingInfo.city}
              </div>
              <div className={style.AddressItem}>
                <VisuallyHidden>State:</VisuallyHidden>
                {order.shippingInfo.state}
              </div>
              <div className={style.AddressItem}>
                <VisuallyHidden>Postal code:</VisuallyHidden>
                {order.shippingInfo.postalCode}
              </div>
              <div className={style.AddressItem}>
                <VisuallyHidden>Country:</VisuallyHidden>
                {countries[order.shippingInfo.country as unknown as TCountryCode].name}
              </div>
            </div>
          )}
        </div>
        <OrderItemGroups items={order.lineItems} />
      </div>
      <div className={style.Divider} />
      <div className={style.DeliveryInfo}>
        <div className={style.DeliveryItems}>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Subtotal ({order.lineItems.length} items)
              <VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={subtotal}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Delivery Fee<VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={deliveryFee}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Estimated tax to be collected<VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={tax}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          {roundUp.gt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>
                Donate change to NFB<VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={style.DeliveryItemPrice}>
                <Price
                  price={roundUp}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          )}
          {discountValue.lt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>
                Discount (Total)<VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={clsx(style.DeliveryItemPrice, style.Discount)}>
                <Price
                  price={discountValue}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={style.Divider} />
      <div className={style.Total}>
        <span>Total</span>
        <span>
          <Price
            price={total}
            options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
          />
        </span>
      </div>
      {cashback && (
        <div className={clsx(style.DeliveryInfo, style.Rewards)}>
          <div className={style.DeliveryItems}>
            <div className={style.DeliveryItem}>
              <div className={clsx(style.DeliveryItemName, style.Good)}>
                Rewards ({cashback.rate.mul(100).toNumber()}%)<VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={clsx(style.DeliveryItemPrice, style.Discount)}>
                <Price
                  price={cashback.value}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <PrimaryButton className={style.DoneBtn} onPress={onDone}>
        Done
      </PrimaryButton>
    </div>
  )
}

const selectCheckoutOrderWithItemsAndValue = (data: Order | false | string) => {
  if (typeof data === 'string' || data === false) return data

  return {
    ...data,
    items: groupBy(data.lineItems, item => item.retailer),
    value: data.lineItems.reduce(
      (acc, item) => acc.add(Big(item.price).times(item.quantity)),
      Big(0),
    ),
  }
}

export default memo(function Checkout() {
  const {result} = CheckoutRoute.useParams()
  const {orderId} = CheckoutRoute.useSearch()
  const [locale] = useLocale()

  invariant(orderId, 'orderId is required')

  const {data: order, isLoading} = useQuery(
    getOrderQueryOptions(
      {locale, orderId},
      {
        select: selectCheckoutOrderWithItemsAndValue,
      },
    ),
  )

  if (
    !isLoading &&
    (!Object.values(CheckoutResult).includes(result) || !order || typeof order === 'string')
  ) {
    return <NotFound />
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- result is not null
  const parsedResult = Object.values(CheckoutResult).find(
    item => typeof item === 'string' && item.includes(result),
  )!

  return (
    <>
      <HeadTags
        title={
          parsedResult === CheckoutResult.Success ? 'Checkout successfully' : 'Checkout failed'
        }
      />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar transparentBg noLogo variant='light' />
        <main className={style.CheckoutPage} {...skipTargetProps('main-content')}>
          <div className={style.CheckoutContent}>
            <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
              <Breadcrumbs className={style.Breadcrumbs}>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={HomeRoute.fullPath}>
                    Home
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={CartRoute.fullPath}>
                    Cart
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={CheckoutRoute.fullPath} params={{result}}>
                    Checkout
                  </RACLink>
                </Breadcrumb>
              </Breadcrumbs>
            </div>
            {isLoading && (
              <div className={style.SpinnerContainer}>
                <DotLottieReact
                  src={spinnerLottie}
                  autoplay
                  loop
                  className={style.Spinner}
                  aria-label='Loading product...'
                  role='progressbar'
                />
              </div>
            )}
            {!isLoading && order && typeof order !== 'string' && (
              <Result parsedResult={parsedResult} order={order} />
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  )
})
